import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import Lenis from "lenis";
import { motion } from "framer-motion";
import {
  Film, Scissors, Wand2, Music,
  Sparkles, Zap, ArrowRight, Play, Star
} from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Process from "../components/Process";
import SEO from "../components/SEO";
import EditWorkshop from "../components/EditWorkshop";
import SectionStepper from "../components/SectionStepper";

const editingSteps = [
  {
    step: "01",
    title: "Ingest",
    desc: "Organizing high-bitrate footage and creating the first 'rough cut' to establish the narrative flow and timing.",
    image: "https://images.pexels.com/photos/27561824/pexels-photo-27561824/free-photo-of-railway-line-in-lungern.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1"
  },
  {
    step: "02",
    title: "First Cut",
    desc: "Refining every transition and frame. This is where we dial in the pacing and emotional resonance.",
    image: "https://images.unsplash.com/photo-1550745165-9bc0b252726f?auto=format&fit=crop&q=80&w=1200"
  },
  {
    step: "03",
    title: "Color & Sound",
    desc: "Professional color grading and immersive sound design to elevate the visual story.",
    image: "https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?auto=format&fit=crop&q=80&w=1200"
  },
  {
    step: "04",
    title: "Mastering",
    desc: "Mastering for various platforms, ensuring peak quality from cinema screens to mobile devices.",
    image: "https://images.unsplash.com/photo-1626785774573-4b799315345d?auto=format&fit=crop&q=80&w=1200"
  }
];

const VideoEditing = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    const lenis = new Lenis({
      duration: 1.15,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true
    });
    const raf = (time) => {
      lenis.raf(time);
      requestAnimationFrame(raf);
    };
    requestAnimationFrame(raf);
    return () => lenis.destroy();
  }, []);

  const stepperSections = [
    { id: "hero", label: "Timeline" },
    { id: "capabilities", label: "Post-Pro" },
    { id: "process", label: "Workflow" },
    { id: "workshop", label: "Edit Studio" }
  ];

  return (
    <div className="bg-[var(--ink)] text-[var(--cream)] min-h-screen">
      <SEO
        title="Broadcast-Grade Video Editing"
        description="Professional video editing, color grading, and sound design. We turn raw footage into cinematic stories."
      />
      <SectionStepper sections={stepperSections} />
      <div className="grain" />
      <Navbar />

      <main>
        {/* Hero */}
        <section id="hero" className="relative min-h-screen flex items-center pt-20 overflow-hidden">
          <div className="absolute inset-0 z-0">
            <div className="absolute inset-0 bg-gradient-to-b from-[var(--ink)] via-transparent to-[var(--ink)] z-10" />
            <motion.div
              initial={{ scale: 1.1, opacity: 0 }}
              animate={{ scale: 1, opacity: 0.55 }}
              transition={{ duration: 1.5 }}
              className="w-full h-full"
            >
              <video
                autoPlay
                muted
                loop
                playsInline
                preload="auto"
                className="w-full h-full object-cover grayscale brightness-75"
              >
                <source src="https://videos.pexels.com/video-files/29380116/12656384_1080_1920_30fps.mp4" type="video/mp4" />
              </video>
            </motion.div>
          </div>

          <div className="container-xl w-full relative z-20 px-6">
            <motion.div
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="max-w-5xl"
            >
              <span className="font-impact text-[var(--gold)] tracking-[0.4em] uppercase text-sm mb-6 block">
                Precision Cutting
              </span>
              <h1 className="font-display text-6xl md:text-8xl lg:text-9xl mb-8 leading-tight">
                Mastering the <br />
                <span className="italic font-serif-accent gold-text">Timeline</span>
              </h1>
              <p className="max-w-2xl text-[var(--cream-muted)] text-lg md:text-xl mb-12 font-body leading-relaxed">
                Editing is where the film is truly born. We combine technical rigour with artistic intuition to create seamless, impactful narratives from your raw footage.
              </p>
              <div className="flex flex-col md:flex-row items-start justify-start gap-6">
                <Link to="/contact" className="btn-primary">
                  Edit Your Story <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
                <a href="#workshop" className="btn-ghost">
                  Enter Workshop
                </a>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Services Grid */}
        <section id="capabilities" className="py-32 relative">
          <div className="container-xl px-6">
            <Kicker index="01" label="Capabilities" />
            <h2 className="mt-8 font-display text-4xl md:text-6xl text-[var(--cream)] mb-20">
              Post-Production <span className="italic gold-text">Excellence</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                { 
                  icon: <Scissors />, 
                  title: "Narrative Editing", 
                  desc: "Crafting story-driven sequences for film and television.",
                  video: "https://videos.pexels.com/video-files/3249935/3249935-uhd_2560_1440_25fps.mp4"
                },
                { 
                  icon: <Wand2 />, 
                  title: "Color Grading", 
                  desc: "Setting the visual mood with professional color correction and grading.",
                  video: "https://videos.pexels.com/video-files/3252061/3252061-uhd_2560_1440_25fps.mp4"
                },
                { 
                  icon: <Music />, 
                  title: "Sound Design", 
                  desc: "Immersive audio landscapes and professional mixing.",
                  video: "https://videos.pexels.com/video-files/3195394/3195394-uhd_2560_1440_25fps.mp4"
                }
              ].map((s, i) => (
                <div 
                  key={i} 
                  className="relative group p-10 bg-[var(--ink-2)] border border-[var(--rule)] rounded-2xl overflow-hidden transition-all duration-700 min-h-[300px] flex flex-col justify-end"
                >
                  {/* Background Video */}
                  <div className="absolute inset-0 z-0">
                    <div className="absolute inset-0 bg-[var(--ink)]/80 group-hover:bg-[var(--ink)]/40 transition-colors duration-700 z-10" />
                    <video
                      autoPlay
                      muted
                      loop
                      playsInline
                      preload="auto"
                      className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700 opacity-20 group-hover:opacity-60 scale-110 group-hover:scale-100"
                    >
                      <source src={s.video} type="video/mp4" />
                    </video>
                  </div>

                  <div className="relative z-20">
                    <div className="text-[var(--gold)] mb-6 transform group-hover:scale-110 transition-transform origin-left duration-500">{s.icon}</div>
                    <h3 className="font-display text-2xl mb-4 text-[var(--cream)]">{s.title}</h3>
                    <p className="text-[var(--muted)] group-hover:text-[var(--cream)] text-sm leading-relaxed transition-colors duration-500">{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <Process id="process" steps={editingSteps} title="The Editing Workflow" kicker={{ index: "02", label: "The Pipeline" }} />

        <EditWorkshop />
      </main>

      <Footer />
    </div>
  );
};

const Kicker = ({ index, label }) => (
  <div className="flex items-center gap-4 mb-4">
    <span className="font-impact text-[11px] tracking-[0.4em] text-[var(--gold)]">{index}</span>
    <span className="w-16 h-px bg-[var(--rule)]" />
    <span className="font-impact text-[11px] tracking-[0.4em] text-[var(--cream-muted)] uppercase">{label}</span>
  </div>
);

export default VideoEditing;
